import { PayloadAction } from '@reduxjs/toolkit';
import { errorMessages } from '~/constants/errorMessages.ts';
import {
  TCompletedForms,
  TCreateApplicationResponse,
  TGenerateResponseDocs,
  TIdentificationForm,
  TIdentificationStore,
  TSyncAdditionalInfoResponse,
  TUpdateFormResponse,
  TValidateBeforeSigning,
} from '../types';
import {
  DeletionPermissionEnum,
  DocStatusEnum,
  TDocument,
  TDocumentsInStore,
} from '~/modules/identification/forms/Documents/types';
import {
  TAdditionalInformationFormValues,
  TPepInStore,
} from '~/modules/identification/forms/AdditionalInformation/types';
import { TManagersAndRepresentatives } from '~/modules/identification/forms/ManagersAndRepresentatives/types';
import { ClientTypes } from '~/constants/clientTypes.ts';
import { TGeneralInfoSoleTrader } from '~/modules/identification/forms/GeneralInfo/types.ts';
import { TGettingStarted } from '~/modules/identification/forms/GettingStarted/types';
import { TOwnershipStructure } from '~/modules/identification/forms/OwnershipStructure/types';
import { TManagmentStructure } from '~/modules/identification/forms/ManagementStructure/types';
import { ROOT_OWNER_NODE } from '~/modules/identification/forms/OwnershipStructure/constants';
import { EMPTY_ERROR } from '~/constants/errorCodes';
import { TStoreErrorAction } from '~/store/types';

export const setLoadingAction = (state: TIdentificationStore) => {
  state.loading = true;
  state.error = EMPTY_ERROR;
};

export const setErrorAction = (state: TIdentificationStore, action: TStoreErrorAction) => {
  const error =
    (action.payload as { message: string })?.message ??
    action.error?.message ??
    errorMessages.SERVICE_UNAVAILABLE;

  state.loading = false;
  state.error.errorMessage = error;
};

export const setIsAssetManagersChanged = (
  state: TIdentificationStore,
  action: PayloadAction<boolean>
) => {
  (
    state.formData.managersAndRepresentatives as TManagersAndRepresentatives
  ).isAnyAssetManagerDataChanged = action.payload;
  state.error = EMPTY_ERROR;
};

export const setMainDocuments = (
  state: TIdentificationStore,
  action: PayloadAction<TUpdateFormResponse>
) => {
  const mainDocs = [...(state.formData.documents as TDocumentsInStore).documents.mainDocuments];

  const indexesByWebId = new Map<number, number[]>();
  mainDocs.forEach((doc, index) => {
    const webId = doc.idDictWebDocument;
    if (!indexesByWebId.has(webId)) indexesByWebId.set(webId, []);
    indexesByWebId.get(webId)!.push(index);
  });

  Object.keys(action.payload.newDocuments).forEach((keyStr) => {
    const key = Number(keyStr);
    const idDocs = action.payload.newDocuments[
      key as keyof typeof action.payload.newDocuments
    ] as number[];
    const availableIndexes = indexesByWebId.get(key) ?? [];

    idDocs?.forEach((idDoc, i) => {
      const index = availableIndexes[i];
      if (index !== undefined) {
        mainDocs[index] = {
          ...mainDocs[index],
          docStatus:
            mainDocs[index]?.idDoc === idDoc
              ? mainDocs[index]?.docStatus
              : DocStatusEnum.NOT_VERIFIED,
          idDoc: idDoc,
          idDictWebDocument: mainDocs[index]?.idDictWebDocument ?? key,
          nameDoc: mainDocs[index]?.nameDoc ?? '',
        };
      } else {
        mainDocs.push({
          idDictWebDocument: key,
          idDoc: idDoc,
          docStatus: DocStatusEnum.NOT_VERIFIED,
          deletionPermission: DeletionPermissionEnum.ALLOWED,
          nameDoc: '',
        });
      }
    });
  });

  const deletedDocuments = action.payload.deletedDocuments as TGenerateResponseDocs;
  const newMainDocs = mainDocs.filter((doc) => {
    const deletedIds = deletedDocuments[doc.idDictWebDocument];
    if (!deletedIds) return true;
    return !deletedIds.includes(doc.idDoc);
  });

  state.loading = false;
  state.error = EMPTY_ERROR;
  (state.formData.documents as TDocumentsInStore).documents.mainDocuments = newMainDocs;
};

export const updateAdditionalInfoFulfilledAction = (
  state: TIdentificationStore,
  action: PayloadAction<TSyncAdditionalInfoResponse>
) => {
  state.loading = false;
  state.error = EMPTY_ERROR;
  (state.formData.completedForms as TCompletedForms).isAdditionalInformationCompleted =
    action.payload.isCompleted;
  (state.formData.additionalInformation as TPepInStore).additionalInformation =
    action.payload.additionalInformation;
};

export const setFormData = (
  state: TIdentificationStore,
  action: PayloadAction<TIdentificationForm>
) => {
  state.loading = false;
  state.error = EMPTY_ERROR;
  if (action.payload.type === ClientTypes.LEGAL_RESIDENT) {
    const ownershipStructure = action.payload.ownershipStructure as
      Partial<TOwnershipStructure> | undefined;
    const owners = Array.isArray(ownershipStructure?.owners) ? ownershipStructure.owners : [];
    const link = Array.isArray(ownershipStructure?.link) ? ownershipStructure.link : [];
    const form: TIdentificationForm = {
      ...action.payload,
      ownershipStructure: {
        ...ownershipStructure,
        isBeneficiariesAbsent: !owners.some((item) => item.isBeneficiary),
        owners: owners.map((item) => ({
          ...item,
          ownershipPercentage:
            link.find((value) => value.fromNodeId === item.id)?.ownershipPercentage || null,
        })),
        link,
        ownershipPercentageTotal: link.reduce(
          (prev, current) =>
            current.toNodeId === ROOT_OWNER_NODE
              ? prev + +(current.ownershipPercentage || 0)
              : prev,
          0
        ),
      },
      managersAndRepresentatives: {
        ...action.payload.managersAndRepresentatives,
        assetManagers: (
          action.payload.managersAndRepresentatives as TManagersAndRepresentatives
        )?.assetManagers?.sort((a, b) => (b.isHead ? 1 : 0) - (a.isHead ? 1 : 0)),
      },
    };

    state.formData = form;
    state.formDataBack = form;
  } else {
    state.formData = action.payload;
    state.formDataBack = action.payload;
  }
};

export const setApplicationId = (
  state: TIdentificationStore,
  action: PayloadAction<TCreateApplicationResponse>
) => {
  state.loading = false;
  state.error = EMPTY_ERROR;
  state.formData.applicationId = action.payload.id;
};

export const validateBeforeSigningFulfilled = (
  state: TIdentificationStore,
  action: PayloadAction<TValidateBeforeSigning>
) => {
  state.loading = false;
  state.error = EMPTY_ERROR;
  (state.formData.documents as TDocumentsInStore) = {
    ...(state.formData.documents as TDocumentsInStore),
    readyForSigning: action.payload.readyForSigning,
    missingForms: action.payload.missingForms,
    missingDocuments: action.payload.missingDocuments,
  };
};

export const fulfilledAction = (state: TIdentificationStore) => {
  state.loading = false;
  state.error = EMPTY_ERROR;
};

export const getAssetManagerDocsFulfilled = (
  state: TIdentificationStore,
  action: PayloadAction<TDocument[]>
) => {
  state.loading = false;
  state.error = EMPTY_ERROR;
  (state.formData.documents as TDocumentsInStore).documents.assetManagersDocuments = action.payload;
};

export const setFormDataChangeVideoVerification = (
  state: TIdentificationStore,
  action: PayloadAction<TIdentificationForm>
) => {
  state.loading = false;
  state.error = EMPTY_ERROR;
  const fop = (state.formData?.gettingStarted as TGettingStarted)?.type === ClientTypes.SOLE_TRADER;
  if (fop) {
    (state.formData.gettingStarted as TGettingStarted).verification = (
      action.payload.gettingStarted as TGettingStarted
    ).verification;
    (state.formData.generalInfo as TGeneralInfoSoleTrader).address.citizenshipCountry = (
      action.payload.generalInfo as TGeneralInfoSoleTrader
    ).address.citizenshipCountry;
    (state.formData.generalInfo as TGeneralInfoSoleTrader).birthPlace = (
      action.payload.generalInfo as TGeneralInfoSoleTrader
    ).birthPlace;
    (state.formData.generalInfo as TGeneralInfoSoleTrader).uniqueDemographicRecordNumber = (
      action.payload.generalInfo as TGeneralInfoSoleTrader
    ).uniqueDemographicRecordNumber;
    (state.formData.generalInfo as TGeneralInfoSoleTrader).documents.documentNumber = (
      action.payload.generalInfo as TGeneralInfoSoleTrader
    ).documents.documentNumber;
    (state.formData.generalInfo as TGeneralInfoSoleTrader).documents.issueDate = (
      action.payload.generalInfo as TGeneralInfoSoleTrader
    ).documents.issueDate;
    (state.formData.generalInfo as TGeneralInfoSoleTrader).documents.issuingAuthority = (
      action.payload.generalInfo as TGeneralInfoSoleTrader
    ).documents.issuingAuthority;
    (state.formData.generalInfo as TGeneralInfoSoleTrader).documents.documentExpiryDate = (
      action.payload.generalInfo as TGeneralInfoSoleTrader
    ).documents.documentExpiryDate;
    (state.formData.generalInfo as TGeneralInfoSoleTrader).documents.documentType = (
      action.payload.generalInfo as TGeneralInfoSoleTrader
    ).documents.documentType;
    (state.formData.generalInfo as TGeneralInfoSoleTrader).documents.registryRecord = (
      action.payload.generalInfo as TGeneralInfoSoleTrader
    ).documents.registryRecord;
    (state.formData.generalInfo as TGeneralInfoSoleTrader).documents.documentSeries = (
      action.payload.generalInfo as TGeneralInfoSoleTrader
    ).documents.documentSeries;
    (state.formData.documents as TDocumentsInStore).documents = (
      action.payload.documents as TDocumentsInStore
    ).documents;
  } else {
    (state.formData.gettingStarted as TGettingStarted).verification = (
      action.payload.gettingStarted as TGettingStarted
    ).verification;
    (state.formData.managementStructure as TManagmentStructure).birthPlace = (
      action.payload.managementStructure as TManagmentStructure
    ).birthPlace;
    (state.formData.managementStructure as TManagmentStructure).identificationNumber = (
      action.payload.managementStructure as TManagmentStructure
    ).identificationNumber;
    (state.formData.managementStructure as TManagmentStructure).documents.documentNumber = (
      action.payload.managementStructure as TManagmentStructure
    ).documents.documentNumber;
    (state.formData.managementStructure as TManagmentStructure).documents.issueDate = (
      action.payload.managementStructure as TManagmentStructure
    ).documents.issueDate;
    (state.formData.managementStructure as TManagmentStructure).documents.issuingAuthority = (
      action.payload.managementStructure as TManagmentStructure
    ).documents.issuingAuthority;
    (state.formData.managementStructure as TManagmentStructure).documents.documentExpiryDate = (
      action.payload.managementStructure as TManagmentStructure
    ).documents.documentExpiryDate;
    (state.formData.managementStructure as TManagmentStructure).documents.documentType = (
      action.payload.managementStructure as TManagmentStructure
    ).documents.documentType;
    (state.formData.managementStructure as TManagmentStructure).documents.registryRecord = (
      action.payload.managementStructure as TManagmentStructure
    ).documents.registryRecord;
    (state.formData.managementStructure as TManagmentStructure).documents.documentSeries = (
      action.payload.managementStructure as TManagmentStructure
    ).documents.documentSeries;
    (state.formData.documents as TDocumentsInStore).documents = (
      action.payload.documents as TDocumentsInStore
    ).documents;
  }
};

export const setFormFatcaAdditionalInfo = (
  state: TIdentificationStore,
  action: PayloadAction<TIdentificationForm & { isCompleted?: boolean }>
) => {
  state.loading = false;
  state.error = EMPTY_ERROR;
  const fop = (state.formData?.gettingStarted as TGettingStarted)?.type === ClientTypes.SOLE_TRADER;
  if (fop) {
    (state.formData.additionalInformation as TAdditionalInformationFormValues) = action.payload
      .additionalInformation as TAdditionalInformationFormValues;
    if (typeof action.payload.isCompleted === 'boolean') {
      state.formData.completedForms.isAdditionalInformationCompleted = action.payload.isCompleted;
    }
  } else {
    state.formData.fatcaAdditionalInformation = action.payload.fatcaAdditionalInformation;
    if (typeof action.payload.isCompleted === 'boolean') {
      state.formData.completedForms.isFatcaAdditionalInformationCompleted =
        action.payload.isCompleted;
    }
  }
};
